import React, {
  ChangeEvent,
  DragEvent,
  FC,
  useCallback,
  useRef,
  useState,
} from "react";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { IconType } from "enums/Icon";
import { List, Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import Button from "../../../../components/Button/Button";
import Modal from "../../../../components/Modal";
import {
  getAddressesCsvExample,
  importAddressesCsv,
} from "../../../../reduxFolder/reducers/destinations";
import { downloadCsvFile } from "../../../../utils/downloadCsvFile";
import {
  toastResponseError,
  toastResponseSuccess,
} from "../../../../utils/responseMessageHelper";
import { toastImportAddressesErrors } from "../../helpers/toastImportAddressesErrors";
import {
  DropZone,
  DropZoneHint,
  DropZoneIcon,
  DropZoneText,
} from "./ImportAddressesButton.style";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const CSV_EXAMPLE_FILENAME = "destination_addresses_example.csv";

interface ImportAddressesButtonProps {
  importAddressesCsv: (data: {
    file: File;
  }) => Promise<{ value: Map<string, unknown> }>;
  getAddressesCsvExample: () => Promise<{ value: Map<string, unknown> }>;
}

export const ImportAddressesButton: FC<ImportAddressesButtonProps> = React.memo(
  ({ importAddressesCsv, getAddressesCsvExample }) => {
    const { t } = useTranslation("common");
    const inputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const close = useCallback(() => {
      setIsOpen(false);
      setIsDragging(false);
    }, []);

    const importFile = useCallback(
      (file: File | null) => {
        if (!file || isImporting) return;

        if (!file.name.toLowerCase().endsWith(".csv")) {
          toastResponseError(t("books.importAddressesWrongFormat"));
          return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
          toastResponseError(t("books.importAddressesTooLarge"));
          return;
        }

        setIsImporting(true);
        importAddressesCsv({ file })
          .then(({ value }) => {
            const imported = value.get("data");
            toastResponseSuccess(
              t("books.importAddressesSuccess", {
                amount: List.isList(imported) ? imported.size : 0,
              }),
            );
            close();
          })
          .catch((error) => toastImportAddressesErrors(error, t))
          .finally(() => setIsImporting(false));
      },
      [close, importAddressesCsv, isImporting, t],
    );

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;
        // reset synchronously so re-selecting the same fixed file re-fires onChange
        event.target.value = "";
        importFile(file);
      },
      [importFile],
    );

    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
      // dragleave also fires when the pointer moves between the zone's children
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setIsDragging(false);
      }
    }, []);

    const handleDrop = useCallback(
      (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        importFile(event.dataTransfer?.files?.[0] ?? null);
      },
      [importFile],
    );

    // The example endpoint sits behind the token-auth headers, so it has to
    // go through the api client and become a client-side download.
    const handleDownloadExample = useCallback(() => {
      getAddressesCsvExample()
        .then(({ value }) => {
          const csv = value.get("data");
          if (typeof csv === "string") {
            downloadCsvFile(csv, CSV_EXAMPLE_FILENAME);
          }
        })
        .catch(toastResponseError);
    }, [getAddressesCsvExample]);

    return (
      <>
        <Button color={ButtonColor.Primary} onClick={() => setIsOpen(true)}>
          {t("books.importAddresses")}
        </Button>
        <Modal
          isOpen={isOpen}
          onClose={close}
          closeDisabled={isImporting}
          title={t("books.importAddresses")}
          footer={
            <Button
              type={ButtonType.Button}
              color={ButtonColor.Primary}
              onClick={handleDownloadExample}
            >
              {t("books.importAddressesDownloadExample")}
            </Button>
          }
        >
          <DropZone
            data-testid="csv-dropzone"
            $isDragging={isDragging}
            onDragEnter={handleDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={handleChange}
            />
            <DropZoneIcon type={IconType.FileCSV} />
            <DropZoneText>{t("books.importAddressesDrop")}</DropZoneText>
            <Button
              variant={ButtonVariant.Bordered}
              color={ButtonColor.Neutral}
              type={ButtonType.Button}
              isLoading={isImporting}
              disabled={isImporting}
              onClick={() => inputRef.current?.click()}
            >
              {t("books.importAddressesChooseFile")}
            </Button>
            <DropZoneHint>{t("books.importAddressesFormats")}</DropZoneHint>
          </DropZone>
        </Modal>
      </>
    );
  },
);

const withConnect = connect(null, {
  importAddressesCsv,
  getAddressesCsvExample,
});

export default withConnect(ImportAddressesButton);
